<template>
  <div
    :id="`${source}_audio_attachment_display`"
    class="flex items-center gap-2 w-full"
  >
    <div
      class="flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer flex-shrink-0 bg_8 border_4"
      @click="togglePlayPause"
    >
      <ThemeImage
        :icon="
          isPlaying
            ? imageNames.PAUSE_VARIANT1_ICON
            : imageNames.PLAY_VARIANT2_ICON
        "
        :class="isPlaying ? 'size-5' : 'size-6'"
        alt="play_pause_icon"
      />
    </div>

    <div class="flex items-center gap-3 w-full">
      <span class="text-sm secondary_color whitespace-nowrap">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </span>

      <input
        type="range"
        min="0"
        :max="duration || 100"
        :value="currentTime"
        @input="handleSeek"
        class="audio_slider w-full"
        :style="`background: linear-gradient(to right, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) ${progress}%, #d3d3d3 ${progress}%, #d3d3d3 100%)`"
      />
    </div>

    <audio
      ref="audioRef"
      :src="attachment.url"
      @timeupdate="updateProgress"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
    ></audio>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  source: { type: String, required: true },
  attachment: { type: Object, required: true },
});

const audioRef = ref(null);
const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const progress = computed(() => {
  if (duration.value === 0) return 0;
  return (currentTime.value / duration.value) * 100;
});

const togglePlayPause = () => {
  try {
    if (!audioRef.value) return;

    if (isPlaying.value) {
      audioRef.value.pause();
    } else {
      audioRef.value.play();
    }
    isPlaying.value = !isPlaying.value;
  } catch (error) {
    console.error("An error occurred while toggling play pause: ", error);
  }
};

const updateProgress = () => {
  try {
    if (!audioRef.value) return;
    currentTime.value = audioRef.value.currentTime;
  } catch (error) {
    console.error("An error occurred while updating progress: ", error);
  }
};

const onLoadedMetadata = () => {
  try {
    if (!audioRef.value) return;
    duration.value = audioRef.value.duration;
  } catch (error) {
    console.error("An error occurred while loading metadata: ", error);
  }
};

const onEnded = () => {
  try {
    isPlaying.value = false;
    currentTime.value = 0;
  } catch (error) {
    console.error("An error occurred while handling ended: ", error);
  }
};

const handleSeek = (event) => {
  try {
    if (!audioRef.value) return;
    const newTime = parseFloat(event.target.value);
    audioRef.value.currentTime = newTime;
    currentTime.value = newTime;
  } catch (error) {
    console.error("An error occurred while seeking: ", error);
  }
};

const seek = (event) => {
  try {
    if (!audioRef.value) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    audioRef.value.currentTime = percent * duration.value;
  } catch (error) {
    console.error("An error occurred while seeking: ", error);
  }
};

const formatTime = (seconds) => {
  try {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  } catch (error) {
    console.error("An error occurred while formatting time: ", error);
    return "0:00";
  }
};
</script>

<style scoped>
.audio_slider {
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  outline: none;
  opacity: 0.9;
  transition: opacity 0.2s;
  border-radius: 3px;
  cursor: pointer;
}

.audio_slider:hover {
  opacity: 1;
}

.audio_slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 0;
  height: 0;
  cursor: pointer;
}

.audio_slider::-moz-range-thumb {
  width: 0;
  height: 0;
  cursor: pointer;
  border: none;
  background: transparent;
}
</style>
